/**
 * Utilitaires SEO / catch-all : chemins parent, logs,
 * détection de payloads vides et décision de redirection vers l'URL parente.
 * (Statut HTTP Axios : `getAxiosResponseStatus` dans `~/utils/axios`.)
 */
import type { ArticleData } from '~/types/article'
import type { ProfessionData } from '~/types/profession'
import type { ProgramData, SchoolData, SchoolDataResponse, SchoolPrograms } from '~/types/school'

// ── Log & construction URL parente ───────────────────────────────────────

export interface InvalidSeoPathLogPayload {
  fromPath: string
  toPath: string
  locale: string
  source: string
  httpStatus?: number
}

/**
 * Réservé au branchement futur (blob / API) ; en dev, log léger.
 * @param {InvalidSeoPathLogPayload} invalidPathLog Contexte de la redirection parente (chemins, locale, source).
 */
export function logInvalidSeoPath(invalidPathLog: InvalidSeoPathLogPayload): void {
  if (import.meta.dev) {
    console.info('[invalid-seo-path]', invalidPathLog)
  }
}

/**
 * Options pour construire l'URL parente d'une section catch-all (`[...slug]`).
 */
export interface BuildSeoParentPathOptions {
  /** Segments du slug après la racine de section (sans le préfixe locale). */
  slugSegments: string[]
  /** Racine de section dans l'URL (ex. `ecoles`, `diplomes`). */
  sectionRoot: string
  /** Code locale courant (ex. `fr`, `en`). */
  locale: string
  /** Locale par défaut de l'app (pas de préfixe d'URL pour celle-ci). */
  defaultLocale: string
}

/**
 * URL parente pour une section SEO catch-all : retire le dernier segment du slug.
 * Respecte le préfixe de locale (ex. `/fr/ecoles/a` → `/fr/ecoles`).
 * @param {BuildSeoParentPathOptions} parentPathOptions Segments slug + racine section + locales.
 * @returns {string | null} Chemin parent absolu, ou `null` si aucun segment slug.
 */
export function buildSeoParentPath(parentPathOptions: BuildSeoParentPathOptions): string | null {
  const { slugSegments, sectionRoot, locale, defaultLocale } = parentPathOptions
  if (slugSegments.length === 0) return null
  const prefix = locale === defaultLocale ? '' : `/${locale}`
  const base = `${prefix}/${sectionRoot}`
  if (slugSegments.length === 1) return base
  return `${base}/${slugSegments.slice(0, -1).join('/')}`
}

const NON_DEFAULT_LOCALE_PREFIXES = new Set(['en', 'es'])

/**
 * Segments du catch-all `[...slug]` (vide sur les pages index de section).
 * @param {string | string[] | undefined} slugParam `route.params.slug`.
 * @returns {string[]} Segments du slug (tableau vide sur index de section).
 */
export function getSlugSegmentsFromRoute(slugParam: string | string[] | undefined): string[] {
  if (Array.isArray(slugParam)) return slugParam
  return slugParam ? [slugParam] : []
}

/**
 * Racine de section + segments slug dérivés de la route courante.
 * @param {string} routePath Chemin complet (`route.path`).
 * @param {string | string[] | undefined} slugParam `route.params.slug`.
 * @returns {{ sectionRoot: string; slugSegments: string[] }} Racine URL et segments catch-all.
 */
export function parseSeoRouteContext(
  routePath: string,
  slugParam: string | string[] | undefined,
): { sectionRoot: string; slugSegments: string[] } {
  const slugSegments = getSlugSegmentsFromRoute(slugParam)
  const pathSegments = routePath.split('/').filter(Boolean)
  if (pathSegments.length > 0 && NON_DEFAULT_LOCALE_PREFIXES.has(pathSegments[0] ?? '')) {
    pathSegments.shift()
  }
  return { sectionRoot: pathSegments[0] ?? '', slugSegments }
}

/**
 * Fiches métier (≥ 3 segments) et articles blog (≥ 2) : redirect gérée ailleurs.
 * @param {string} sectionRoot Premier segment de section après locale (ex. `metiers`).
 * @param {string[]} slugSegments Segments du catch-all `[...slug]`.
 * @returns {boolean} `true` si `useSeoInterData` doit gérer la redirect parente.
 */
export function shouldSeoInterManageParentRedirect(
  sectionRoot: string,
  slugSegments: string[],
): boolean {
  if (sectionRoot === 'metiers' && slugSegments.length >= 3) return false
  if (sectionRoot === 'nos-conseils' && slugSegments.length >= 2) return false
  return true
}

/**
 * URL de redirection : parent d'un niveau slug, ou home si index de section mort.
 * @param {BuildSeoParentPathOptions} parentPathOptions Segments slug + racine section + locales.
 * @returns {string} Chemin absolu de redirection (jamais `null`).
 */
export function resolveSeoParentRedirectPath(parentPathOptions: BuildSeoParentPathOptions): string {
  const parent = buildSeoParentPath(parentPathOptions)
  if (parent) return parent
  const { locale, defaultLocale } = parentPathOptions
  return locale === defaultLocale ? '/' : `/${locale}`
}

// ── État redirect & payloads « morts » ───────────────────────────────────

/** Agrégat passé à `useSeoParentRedirect` : chargement + décision « page morte ». */
export interface SeoParentRedirectState {
  loading: boolean
  isDead: boolean
}

/**
 * Normalise un champ texte optionnel renvoyé par l'API avant de tester s'il est « vide ».
 * Remplace `null` / `undefined` par une chaîne vide, applique `trim` (espaces seuls = vide).
 * Sert uniquement aux helpers `isEmpty*` ci-dessous pour comparer des payloads de façon homogène.
 * @param {string | undefined | null} raw Valeur brute d'un champ (ex. titre, H1).
 * @returns {string} Texte prêt à être testé : `''` signifie « pas de contenu utile ».
 */
function normalizeOptionalText(raw: string | undefined | null): string {
  return (raw ?? '').trim()
}

/**
 * Réponse `schooldata` : coquille HTTP 200 sans école réelle (ex. slug obsolète).
 * @param {SchoolData} schoolPayload Corps métier `SchoolData` renvoyé par l'API.
 * @returns {boolean} `true` si `name` et `h1` sont vides après trim.
 */
export function isEmptySchoolApiPayload(schoolPayload: SchoolData): boolean {
  return !normalizeOptionalText(schoolPayload.name) && !normalizeOptionalText(schoolPayload.h1)
}

/**
 * Fiche école « coquille » : pas de texte de présentation ni de campus renseignés.
 * Conserve aussi le test slug sans identité (`name` / `h1` vides).
 * @param {SchoolDataResponse} schoolResponse Réponse `schooldata` parsée (page détail école).
 * @returns {boolean} `true` si la fiche doit être traitée comme vide (redirection parent).
 */
export function isShellSchoolDataResponse(schoolResponse: SchoolDataResponse): boolean {
  if (isEmptySchoolApiPayload(schoolResponse.schooldatas)) return true

  const hasContentText: boolean =
    normalizeOptionalText(schoolResponse.schooldatas.contentText).length > 0
  const hasCampuses: boolean = (schoolResponse.parsedAddressesBlock.Campuses?.length ?? 0) > 0

  return !hasContentText && !hasCampuses
}

/**
 * Réponse `schoolprograms` : liste sans contenu métier exploitable.
 * @param {SchoolPrograms} schoolProgramsPayload Corps métier `SchoolPrograms` renvoyé par l'API.
 * @returns {boolean} `true` si `name` et `h1` sont vides après trim.
 */
export function isEmptySchoolProgramsApiPayload(schoolProgramsPayload: SchoolPrograms): boolean {
  return (
    !normalizeOptionalText(schoolProgramsPayload.name) &&
    !normalizeOptionalText(schoolProgramsPayload.h1)
  )
}

/**
 * Réponse `programdata` : fiche programme vide ou non exploitable.
 * @param {ProgramData} programPayload Corps métier `ProgramData` renvoyé par l'API.
 * @returns {boolean} `true` si `name` et `h1` sont vides après trim.
 */
export function isEmptyProgramApiPayload(programPayload: ProgramData): boolean {
  return !normalizeOptionalText(programPayload.name) && !normalizeOptionalText(programPayload.h1)
}

/**
 * Fiche métier (`professiondata`) : coquille HTTP 200 sans métier réel.
 * @param {ProfessionData} professionPayload Corps métier `ProfessionData` renvoyé par l'API.
 * @returns {boolean} `true` si `name` et `h1` sont vides après trim.
 */
export function isEmptyProfessionApiPayload(professionPayload: ProfessionData): boolean {
  return (
    !normalizeOptionalText(professionPayload.name) && !normalizeOptionalText(professionPayload.h1)
  )
}

/**
 * Article blog : corps renvoyé vide (slug article invalide ou contenu absent).
 * @param {ArticleData} articlePayload Corps métier `ArticleData` renvoyé par l'API.
 * @returns {boolean} `true` si `h1` et `contentText` sont vides après trim.
 */
export function isEmptyArticleApiPayload(articlePayload: ArticleData): boolean {
  return (
    !normalizeOptionalText(articlePayload.h1) && !normalizeOptionalText(articlePayload.contentText)
  )
}

/** Champs SEO d'une page inter (`/api/aca/seointer`) utilisés pour détecter une page vide. */
export interface SeoInterPageFields {
  h1: string
  title: string
  introduction: string
}

/**
 * Hub / inter SEO (`seointer`) : page niveau sans contenu exploitable.
 * @param {SeoInterPageFields} seoInterPage Champs `h1`, `title`, `introduction` extraits de la réponse SEO inter.
 * @returns {boolean} `true` si les trois champs sont vides après trim.
 */
export function isEmptySeoInterApiPayload(seoInterPage: SeoInterPageFields): boolean {
  return (
    !normalizeOptionalText(seoInterPage.h1) &&
    !normalizeOptionalText(seoInterPage.title) &&
    !normalizeOptionalText(seoInterPage.introduction)
  )
}

/**
 * Critères pour décider si l'utilisateur doit être renvoyé vers l'URL parente (slug plus court).
 */
export interface ShouldRedirectToSeoParentInput {
  /** `true` tant qu'un fetch SEO / ACA est en cours. */
  loading: boolean
  /** Statut HTTP du dernier appel (`undefined` si inconnu ou pas encore défini). */
  httpStatus: number | undefined
  /**
   * Données déjà parsées ou `null` / `undefined` si échec fetch ou absence de payload utile.
   */
  data: unknown
  /**
   * Test métier : la réponse est-elle une « coquille vide » malgré un 2xx éventuel.
   * @param {unknown} payload Donnée passée au test (même référence que `data` en pratique).
   */
  isEmptyPayload: (payload: unknown) => boolean
}

/**
 * Après chargement : faut-il remonter au parent ?
 * — Pas de redirection si erreur serveur (5xx) ou erreur réseau (statut inconnu + pas de données).
 * @param {ShouldRedirectToSeoParentInput} redirectCriteria Chargement, HTTP, données et test « payload vide ».
 * @returns {boolean} `true` si la navigation doit être remplacée par l'URL parente.
 */
export function shouldRedirectToSeoParent(
  redirectCriteria: ShouldRedirectToSeoParentInput,
): boolean {
  if (redirectCriteria.loading) return false
  const httpStatus = redirectCriteria.httpStatus
  if (httpStatus !== undefined && httpStatus >= 500) return false
  if (httpStatus === 404) return true
  if (redirectCriteria.data === null || redirectCriteria.data === undefined) {
    // Pas de redirect sur 200 + data null : état transitoire au changement de route (refetch client).
    if (httpStatus === undefined || httpStatus === 200) return false
    return httpStatus >= 400 && httpStatus < 500
  }
  return redirectCriteria.isEmptyPayload(redirectCriteria.data)
}
